import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePutVendorMutation } from 'api';
import { PutVendorRequest } from 'api/bindings/PutVendorRequest';
import { VendorResponse } from 'api/bindings/VendorResponse';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface EditVendorFormDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  vendor: VendorResponse;
  disallowedVendorNames: string[] | undefined;
}

interface Form extends PutVendorRequest {
  id: string;
}

export const EditVendorFormDrawer = (props: EditVendorFormDrawerProps) => {
  const { vendor, disallowedVendorNames = [], ...rest } = props;

  // Fetches
  const [putVendor] = usePutVendorMutation();

  // State
  const toast = useToast();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: vendor,
  });
  const { reset, watch, control, register, handleSubmit } = form;
  const vendorName = watch('name');

  const isValidName = useCallback(
    (name: string | null) => {
      const disallowedNames = disallowedVendorNames.filter(
        (x) => x !== vendor.name
      );
      return (
        (disallowedNames.some((x) => x.toLowerCase() === name?.toLowerCase()) &&
          `${name} is already in use`) ||
        undefined
      );
    },
    [disallowedVendorNames, vendor.name]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await putVendor(form);
        if ('error' in result) {
          toast({
            title: `Failed to save vendor ${form.name}`,
            description: (result.error as any)?.data?.message,
            status: 'error',
          });
          return;
        }

        toast({
          title: `Vendor ${form.name} saved!`,
          status: 'success',
        });
        reset();
        rest.onClose();
      } catch {}
    },
    [putVendor, reset, rest, toast]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer {...rest} size="xl">
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>Edit Vendor {vendorName}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: `Name is required`,
                      minLength: {
                        value: 3,
                        message: `Name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Name must be at most 256 characters.`,
                      },
                      validate: {
                        isValidName,
                      },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={() => {
                  reset();
                  rest.onClose();
                }}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
